import type {
  ChannelModelRecord,
  CreateChannelModelRequest,
  CreateChannelModelResponse,
  DeleteChannelModelResponse,
  UpdateChannelModelRequest,
  UpdateChannelModelResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { listChannelModels, createChannelModel, deleteChannelModel, updateChannelModel } from './channelModels.ts';

const CM_KEY = ['channel-models'] as const;

export function useChannelModelsList(
  channelId?: string,
  options?: { readonly enabled?: boolean },
): UseQueryResult<readonly ChannelModelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CM_KEY, channelId ?? '__all__'],
    queryFn: async () => {
      const res = await listChannelModels(
        channelId ? { channel_id: channelId } : {},
        fetchImpl,
      );
      return res.channel_models;
    },
    // #1658: `/v1/ops/channel-models` is admin-only (its rows carry
    // procurement cost), so CM-reachable surfaces disable the fetch
    // instead of collecting a guaranteed 403.
    enabled: options?.enabled ?? true,
  });
}

export function useCreateChannelModel(): UseMutationResult<CreateChannelModelResponse, Error, CreateChannelModelRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChannelModelRequest) => createChannelModel(body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CM_KEY }); },
  });
}

/** Epic #898 P6 (#910): SKU pricing PATCH — multiplier / upstream cost. */
export function useUpdateChannelModel(): UseMutationResult<
  UpdateChannelModelResponse,
  Error,
  { skuId: string; body: UpdateChannelModelRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }) => updateChannelModel(skuId, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CM_KEY }); },
  });
}

export function useDeleteChannelModel(): UseMutationResult<
  DeleteChannelModelResponse,
  Error,
  { channelId: string; model: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, model }) =>
      deleteChannelModel(channelId, model, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CM_KEY }); },
  });
}
